import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { UiDrawerComponent } from '../../molecules/drawer/drawer.component';
import {
  CameraVisibleCrop,
  captureHighQualityPhoto,
  prepareCameraVideo,
  requestCameraStream,
  stopMediaStream,
} from '../camera-capture.utils';

export type UiIdentityPhotoCaptureFacingMode = 'environment' | 'user' | '';
export type UiIdentityPhotoCaptureSourceMode = 'camera' | 'file';

let uiIdentityPhotoCaptureUid = 0;

/**
 * Atomo: captura/carga de foto para documentos de identidad.
 *
 * Presentacional: no valida identidad ni sube archivos. La feature consumidora
 * decide como persistir, validar o procesar el archivo emitido.
 */
@Component({
  selector: 'ui-identity-photo-capture',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiDrawerComponent],
  templateUrl: './identity-photo-capture.component.html',
  styleUrl: './identity-photo-capture.component.scss',
  host: {
    class: 'ui-identity-photo-capture-host',
    '[class.ui-identity-photo-capture-host--block]': 'block()',
  },
})
export class UiIdentityPhotoCaptureComponent implements OnDestroy {
  @ViewChild('cameraVideo') private cameraVideo?: ElementRef<HTMLVideoElement>;
  @ViewChild('cameraGuide') private cameraGuide?: ElementRef<HTMLElement>;

  readonly label = input('Foto de identificacion');
  readonly description = input('Toma o carga una foto clara de tu identificacion oficial.');
  readonly buttonLabel = input('Tomar foto');
  readonly retakeLabel = input('Volver a tomar');
  readonly clearLabel = input('Eliminar foto');
  readonly placeholder = input('Sin foto capturada');
  readonly previewAlt = input('Vista previa de la identificacion');

  readonly imageSrc = input<string | null>(null);
  readonly resetKey = input(0);
  readonly accept = input('image/*');
  readonly capture = input<UiIdentityPhotoCaptureFacingMode>('environment');
  readonly sourceMode = input<UiIdentityPhotoCaptureSourceMode>('file');

  /**
   * Opt-in: en modo camara, muestra el video en vivo dentro de un drawer
   * centrado (placement `center`) con fondo negro, en lugar de inline.
   * Default `false` para no alterar los flujos existentes.
   */
  readonly captureInDrawer = input(false);
  readonly cameraDrawerTitle = input('Tomar foto');
  readonly captureLabel = input('Capturar foto');
  readonly focusingLabel = input('Enfocando...');
  readonly processingLabel = input('Procesando foto...');

  readonly required = input(false);
  readonly showOptionalHint = input(false);
  readonly requiredHint = input('Obligatorio');
  readonly optionalHint = input('Opcional');

  readonly disabled = input(false);
  readonly invalid = input(false);
  readonly message = input<string | undefined>(undefined);
  readonly showMessage = input(true);

  readonly block = input(false);
  readonly fieldId = input<string | undefined>(undefined);

  readonly photoSelected = output<File>();
  readonly photoCleared = output<void>();

  private objectUrl: string | null = null;
  private cameraStream: MediaStream | null = null;
  private cameraRequestId = 0;
  private appliedResetKey = 0;

  protected readonly generatedId = `ui-identity-photo-capture-${++uiIdentityPhotoCaptureUid}`;
  protected readonly inputId = computed(() => this.fieldId() ?? this.generatedId);
  protected readonly messageId = computed(() => `${this.inputId()}-message`);
  protected readonly descriptionId = computed(() => `${this.inputId()}-description`);

  protected readonly localPreviewUrl = signal<string | null>(null);
  protected readonly cameraActive = signal(false);
  protected readonly cameraReady = signal(false);
  protected readonly captureInProgress = signal(false);
  protected readonly cameraError = signal('');
  protected readonly cameraDrawerOpen = signal(false);
  protected readonly previewSrc = computed(() => this.localPreviewUrl() ?? this.imageSrc());
  protected readonly hasPreview = computed(() => Boolean(this.previewSrc()));
  protected readonly captureAttr = computed(() => this.capture() || null);
  protected readonly isCameraMode = computed(() => this.sourceMode() === 'camera');
  protected readonly primaryActionLabel = computed(() => {
    if (this.captureInProgress()) {
      return this.processingLabel();
    }
    if (!this.isCameraMode()) {
      return this.hasPreview() ? this.retakeLabel() : this.buttonLabel();
    }

    if (this.cameraActive() && !this.captureInDrawer()) {
      return this.cameraReady() ? 'Capturar foto' : this.focusingLabel();
    }

    return this.hasPreview() ? this.retakeLabel() : this.buttonLabel();
  });

  protected readonly showRequirement = computed(
    () => this.required() || this.showOptionalHint(),
  );

  protected readonly requirementText = computed(() =>
    this.required() ? this.requiredHint() : this.optionalHint(),
  );

  protected readonly ariaDescribedBy = computed(() => {
    const parts: string[] = [];
    if (this.description()) {
      parts.push(this.descriptionId());
    }
    if (this.message() && this.invalid() && this.showMessage()) {
      parts.push(this.messageId());
    }
    return parts.length ? parts.join(' ') : null;
  });

  constructor() {
    effect(() => {
      const resetKey = this.resetKey();
      if (resetKey === this.appliedResetKey) return;

      this.appliedResetKey = resetKey;
      this.stopCamera();
      this.revokeObjectUrl();
      this.localPreviewUrl.set(null);
      this.cameraError.set('');
      this.cameraDrawerOpen.set(false);
    });
  }

  protected onPrimaryAction(fileInput: HTMLInputElement): void {
    if (this.disabled()) return;

    if (!this.isCameraMode()) {
      fileInput.click();
      return;
    }

    if (this.captureInDrawer()) {
      this.openCameraDrawer();
      return;
    }

    if (this.cameraActive()) {
      if (this.cameraReady()) {
        void this.captureCameraPhoto();
      }
      return;
    }

    void this.startCamera();
  }

  protected onCameraDrawerOpenChange(open: boolean): void {
    this.cameraDrawerOpen.set(open);
    if (!open) {
      this.stopCamera();
      this.cameraError.set('');
    }
  }

  protected captureFromDrawer(): void {
    if (!this.cameraReady()) return;
    void this.captureCameraPhoto();
  }

  private openCameraDrawer(): void {
    this.cameraDrawerOpen.set(true);
    void this.startCamera();
  }

  protected onFileChange(event: Event): void {
    if (this.disabled()) return;

    const inputEl = event.target as HTMLInputElement;
    const file = inputEl.files?.[0];
    if (!file) return;

    this.revokeObjectUrl();
    this.objectUrl = URL.createObjectURL(file);
    this.localPreviewUrl.set(this.objectUrl);
    this.photoSelected.emit(file);
    inputEl.value = '';
  }

  protected clearPhoto(): void {
    if (this.disabled()) return;

    this.stopCamera();
    this.revokeObjectUrl();
    this.localPreviewUrl.set(null);
    this.cameraError.set('');
    this.photoCleared.emit();
  }

  ngOnDestroy(): void {
    this.stopCamera();
    this.revokeObjectUrl();
  }

  private async startCamera(): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      this.cameraError.set('Este navegador no permite abrir la camara.');
      return;
    }

    this.cameraError.set('');
    this.revokeObjectUrl();
    this.localPreviewUrl.set(null);
    this.stopCamera();
    const requestId = ++this.cameraRequestId;

    try {
      const facingMode = this.capture() || 'environment';
      const stream = await requestCameraStream(facingMode);
      if (requestId !== this.cameraRequestId) {
        stopMediaStream(stream);
        return;
      }

      this.cameraStream = stream;
      this.cameraActive.set(true);
      this.cameraReady.set(false);

      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
      if (requestId !== this.cameraRequestId) return;

      const video = this.cameraVideo?.nativeElement;
      if (!video) throw new Error('No se encontro el visor de la camara.');

      await prepareCameraVideo(video, stream);
      if (requestId === this.cameraRequestId) {
        this.cameraReady.set(true);
      }
    } catch {
      if (requestId !== this.cameraRequestId) return;
      this.cameraError.set('No se pudo abrir la camara. Revisa permisos o dispositivo.');
      this.stopCamera();
    }
  }

  private async captureCameraPhoto(): Promise<void> {
    if (!this.cameraReady()) {
      this.cameraError.set('Espera un momento mientras la camara enfoca.');
      return;
    }

    const video = this.cameraVideo?.nativeElement;
    if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
      this.cameraError.set('La camara aun no esta lista. Intenta nuevamente.');
      return;
    }

    const stream = this.cameraStream;
    if (!stream) {
      this.cameraError.set('La camara ya no esta disponible. Intenta nuevamente.');
      return;
    }

    this.cameraReady.set(false);
    this.captureInProgress.set(true);
    try {
      const photo = captureHighQualityPhoto(video, this.resolveGuideCrop(video));
      video.pause();
      const blob = await photo;
      if (stream !== this.cameraStream) return;

      const file = new File([blob], `${this.inputId()}.jpg`, { type: 'image/jpeg' });
      this.revokeObjectUrl();
      this.objectUrl = URL.createObjectURL(file);
      this.localPreviewUrl.set(this.objectUrl);
      this.cameraError.set('');
      this.stopCamera();
      this.cameraDrawerOpen.set(false);
      this.photoSelected.emit(file);
    } catch {
      this.cameraError.set('No se pudo procesar la foto. Intenta nuevamente.');
      this.captureInProgress.set(false);
      this.cameraReady.set(true);
      void video.play();
    }
  }

  private resolveGuideCrop(video: HTMLVideoElement): CameraVisibleCrop | undefined {
    const guide = this.cameraGuide?.nativeElement;
    const videoRect = video.getBoundingClientRect();
    const guideRect = guide?.getBoundingClientRect();
    if (!guideRect || videoRect.width <= 0 || videoRect.height <= 0) return undefined;

    return {
      viewportAspectRatio: videoRect.width / videoRect.height,
      x: (guideRect.left - videoRect.left) / videoRect.width,
      y: (guideRect.top - videoRect.top) / videoRect.height,
      width: guideRect.width / videoRect.width,
      height: guideRect.height / videoRect.height,
    };
  }

  private stopCamera(): void {
    this.cameraRequestId += 1;
    if (this.cameraStream) {
      stopMediaStream(this.cameraStream);
      this.cameraStream = null;
    }

    const video = this.cameraVideo?.nativeElement;
    if (video) {
      video.pause();
      video.srcObject = null;
    }

    this.cameraActive.set(false);
    this.cameraReady.set(false);
    this.captureInProgress.set(false);
  }

  private revokeObjectUrl(): void {
    if (!this.objectUrl) return;

    URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = null;
  }
}
